// This is the one .tsx file in telemetry. The app vite/vitest configs compile every .tsx,
// workspace packages included, with `jsxImportSource: "@theme-ui/core"`, which is why
// `@theme-ui/core` is a dependency of this package even though nothing here uses `sx`.
import * as logClient from "@classdojo/log-client";
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  createRef,
  MutableRefObject,
  Component,
  PropsWithChildren,
} from "react";
import * as Telemetry from "./init";
import { setProductArea } from "./productAreaState";
// Type-only import - no runtime code from api-types in bundle
import type { ProductArea } from "@web-monorepo/api-types/util/teamProductAreas";

//---------------------------------------------------------------
// Product areas used in web. TypeScript enforces these are valid
// ProductArea values from api-types via the "satisfies" clause.
// We maintain a separate copy to avoid bundling sensitive team
// config data (Slack channels, PagerDuty tags) from api-types.
//---------------------------------------------------------------
export const ProductAreas = {
  academicYearRecap: "academicYearRecap",
  accounts: "accounts",
  assistant: "assistant",
  attendance: "attendance",
  attendanceAlert: "attendanceAlert",
  avatarSets: "avatarSets",
  beyond: "beyond",
  bulletin: "bulletin",
  calendarEvent: "calendarEvent",
  calendarSubscription: "calendarSubscription",
  classIslands: "classIslands",
  classroom: "classroom",
  communities: "communities",
  communityInvite: "communityInvite",
  connection: "connection",
  dataExport: "dataExport",
  districtAttendance: "districtAttendance",
  districtLeaderOnboarding: "districtLeaderOnboarding",
  districtPortal: "districtPortal",
  dojoIslandsAccess: "dojoIslandsAccess",
  dojoIslandsActivities: "dojoIslandsActivities",
  dojoIslandsOnline: "dojoIslandsOnline",
  dojoIslandsPayments: "dojoIslandsPayments",
  dojoIslandsReports: "dojoIslandsReports",
  dojoMath: "dojoMath",
  feature_switch: "feature_switch",
  fileUpload: "fileUpload",
  gdpr: "gdpr",
  homeActivity: "homeActivity",
  homeConsent: "homeConsent",
  homeIslands: "homeIslands",
  homeRewardsShop: "homeRewardsShop",
  homeawards: "homeawards",
  homeworkHelper: "homeworkHelper",
  ideas: "ideas",
  locales: "locales",
  memories: "memories",
  message: "message",
  moments: "moments",
  monsterCustomizer: "monsterCustomizer",
  monsterverse: "monsterverse",
  notes: "notes",
  notifications: "notifications",
  parentCoach: "parentCoach",
  parentConnectionRequest: "parentConnectionRequest",
  parentInviteRedeem: "parentInviteRedeem",
  parentInviteSend: "parentInviteSend",
  parentOnboarding: "parentOnboarding",
  parentStudent: "parentStudent",
  pdf: "pdf",
  photobooks: "photobooks",
  playerInventory: "playerInventory",
  points: "points",
  portfolio: "portfolio",
  purchase: "purchase",
  push: "push",
  rostering: "rostering",
  rosterUpload: "rosterUpload",
  school: "school",
  schoolDirectory: "schoolDirectory",
  schoolVerification: "schoolVerification",
  session: "session",
  shared: "shared",
  signup: "signup",
  story: "story",
  studentSettings: "studentSettings",
  studentUser: "studentUser",
  synthesisTutor: "synthesisTutor",
  teacherGrowthNotifications: "teacherGrowthNotifications",
  toolkit: "toolkit",
  unknown: "unknown",
  userOnboarding: "userOnboarding",
  userPhotos: "userPhotos",
} as const satisfies { [K in ProductArea]?: K };

//
// Global state  Approach
//
const defaultContextValue = {
  productArea: ProductAreas.unknown,
};

type ProductAreaKeys = keyof typeof ProductAreas;
export type ProductAreaValues = (typeof ProductAreas)[ProductAreaKeys];

type ProductAreaRefValue = ProductAreaValues | null;
type ProductAreaRef = MutableRefObject<ProductAreaRefValue>;

/* eslint-disable-next-line react-refresh/only-export-components */
export const ProductAreaContext = createContext<ProductAreaRef>(createRef<ProductAreaRefValue>());

type ProductAreaContextProviderProps = {
  children: React.ReactNode;
};

export function ProductAreaContextProvider({ children }: ProductAreaContextProviderProps): JSX.Element {
  const productArea = useRef<ProductAreaValues>(defaultContextValue.productArea);

  return <ProductAreaContext.Provider value={productArea}>{children}</ProductAreaContext.Provider>;
}

const useProductAreaContext = () => {
  return useContext(ProductAreaContext);
};

/**
 * Wraps a Component and sets the app current product area when the component is rendered.
 * This is intended to be used for wrapping route components as it will set the product area in
 * the AppTopLevelErrorBoundary.
 * @param {JSXElement} Component
 * @param {string} productArea - one of the ProductAreas
 */
// eslint-disable-next-line react-refresh/only-export-components
export const withTopLevelProductArea = (Component: React.ComponentType, productArea: ProductAreaValues) => {
  return (props: Record<string, unknown>): JSX.Element => (
    <WithTopLevelProductArea productArea={productArea}>
      <Component {...props} />
    </WithTopLevelProductArea>
  );
};

export const WithTopLevelProductArea: React.FC<PropsWithChildren<{ productArea: ProductAreaValues }>> = ({
  productArea,
  children,
}: PropsWithChildren<{ productArea: ProductAreaValues }>) => {
  const productAreaRef = useProductAreaContext();

  useEffect(() => {
    setProductArea(productAreaRef, productArea);
  }, [productAreaRef, productArea]);

  return <>{children}</>;
};

/**
 * This HoC should be used whenever we want to override the top level product
 * area in a component subtree. It will set the product area in context and also
 * annotate all errors that happen within the subtree with the specified product area.
 * This relies on the AppTopLevelErrorBoundary to actually log the error.
 * @param {JSXElement} Component
 * @param {string} productArea - one of the ProductAreas
 */
// eslint-disable-next-line react-refresh/only-export-components
export const withProductArea = <P extends object>(
  Component: React.ComponentType<P>,
  productArea: ProductAreaValues,
) => {
  return (props: P): JSX.Element => {
    return (
      <ProductAreaErrorBoundary productArea={productArea}>
        <Component {...props} />
      </ProductAreaErrorBoundary>
    );
  };
};

type ProductAreaErrorBoundaryProps = {
  productArea: ProductAreaValues;
  children: React.ReactNode;
};

class ProductAreaErrorBoundary extends Component<ProductAreaErrorBoundaryProps> {
  constructor(props: ProductAreaErrorBoundaryProps) {
    super(props);
    this.productAreaRef = createRef<ProductAreaValues>();
  }

  render() {
    const { productArea, children } = this.props;
    this.productAreaRef.current = productArea;
    logClient.setProductArea(productArea);
    Telemetry.setTag("productArea", productArea);
    return <ProductAreaContext.Provider value={this.productAreaRef}>{children}</ProductAreaContext.Provider>;
  }

  productAreaRef: ProductAreaRef;

  componentDidCatch(e: Error & { productArea?: ProductAreaValues }) {
    if (!e.productArea && this.productAreaRef.current) {
      e.productArea = this.productAreaRef.current;
    }
    throw e;
  }
}
